// File: src/components/ui/NotificationToast.js

import React, { useEffect } from "react";
import {
  Package,
  Truck,
  FileText,
  CreditCard,
  Download,
  Bell,
  AlertCircle,
  Sparkles,
  User,
  Mail,
  ShoppingBag,
  CheckCircle2,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  enqueueToast,
  CustomToast,
  TOAST_PRIORITY,
} from "../../utils/toastUtils";

const NOTIFICATION_TYPES = {
  ORDER: ["order", "order_status", "order_status_update", "order_created", "order_placed"],
  INVOICE: ["invoice", "invoice_created", "invoice_paid"],
  SHIPPING: ["shipping", "shipment", "shipping_update", "shipping_status", "shipping_status_update"],
  PAYMENT: ["payment", "payment_status", "payment_status_update", "payment_success"],
  PAYMENT_FAILED: ["payment_failed", "payment_error"],
  DIGITAL: ["digital", "download_ready"],
  SYSTEM: ["system", "maintenance", "update"],
  REVIEW: [
    "freelancer_service_review",
    "consultation_review",
    "course_review",
    "job_review",
    "seller_product_review",
    "provider_profile_review",
  ],
  PROMO: ["promo", "offer", "discount"],
  USER: ["user", "welcome", "profile_update"],
  EMAIL: ["email", "verification", "reset_password"],
  CHECKOUT: ["checkout", "checkout_success", "order_completed"],
};

function getNotificationMeta(type, isArabic) {
  const t = String(type || "").toLowerCase();

  if (NOTIFICATION_TYPES.ORDER.includes(t) || NOTIFICATION_TYPES.CHECKOUT.includes(t)) {
    return {
      Icon: Package,
      toastType: "warning",
      iconColor: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.15)",
      priority: TOAST_PRIORITY.HIGH,
    };
  }

  if (NOTIFICATION_TYPES.INVOICE.includes(t)) {
    return {
      Icon: FileText,
      toastType: "info",
      iconColor: "#2563eb",
      bgColor: "rgba(59, 130, 246, 0.15)",
      priority: TOAST_PRIORITY.HIGH,
    };
  }

  if (NOTIFICATION_TYPES.SHIPPING.includes(t)) {
    return {
      Icon: Truck,
      toastType: "info",
      iconColor: "#2563eb",
      bgColor: "rgba(59, 130, 246, 0.15)",
      priority: TOAST_PRIORITY.HIGH,
    };
  }

  if (NOTIFICATION_TYPES.PAYMENT.includes(t)) {
    return {
      Icon: CreditCard,
      toastType: "success",
      iconColor: "#16a34a",
      bgColor: "rgba(34, 197, 94, 0.15)",
      priority: TOAST_PRIORITY.HIGH,
    };
  }

  if (NOTIFICATION_TYPES.PAYMENT_FAILED.includes(t)) {
    return {
      Icon: AlertCircle,
      toastType: "error",
      iconColor: "#dc2626",
      bgColor: "rgba(239, 68, 68, 0.15)",
      priority: TOAST_PRIORITY.CRITICAL,
    };
  }

  if (NOTIFICATION_TYPES.DIGITAL.includes(t)) {
    return {
      Icon: Download,
      toastType: "info",
      iconColor: "#7c3aed",
      bgColor: "rgba(124, 58, 237, 0.15)",
      priority: TOAST_PRIORITY.HIGH,
    };
  }

  if (NOTIFICATION_TYPES.PROMO.includes(t)) {
    return {
      Icon: Sparkles,
      toastType: "info",
      iconColor: "#b87d0a",
      bgColor: "rgba(255, 181, 34, 0.2)",
      priority: TOAST_PRIORITY.NORMAL,
    };
  }

  if (NOTIFICATION_TYPES.REVIEW.includes(t)) {
    return {
      Icon: CheckCircle2,
      toastType: "info",
      iconColor: "#2563eb",
      bgColor: "rgba(59, 130, 246, 0.15)",
      priority: TOAST_PRIORITY.HIGH,
    };
  }

  if (NOTIFICATION_TYPES.USER.includes(t)) {
    return {
      Icon: User,
      toastType: "info",
      iconColor: "#2563eb",
      bgColor: "rgba(59, 130, 246, 0.15)",
      priority: TOAST_PRIORITY.NORMAL,
    };
  }

  if (NOTIFICATION_TYPES.EMAIL.includes(t)) {
    return {
      Icon: Mail,
      toastType: "info",
      iconColor: "#2563eb",
      bgColor: "rgba(59, 130, 246, 0.15)",
      priority: TOAST_PRIORITY.NORMAL,
    };
  }

  return {
    Icon: Bell,
    toastType: "info",
    iconColor: "#b87d0a",
    bgColor: "rgba(255, 181, 34, 0.15)",
    priority: TOAST_PRIORITY.HIGH,
  };
}

function enqueueNotificationToast({
  notification,
  title,
  message,
  Icon,
  toastType,
  iconColor,
  bgColor,
  isArabic,
  priority = TOAST_PRIORITY.CRITICAL,
  className = "notification-toast",
  autoClose = 5000,
}) {
  const uniqueKey =
    notification?.__toastKey ||
    notification?.id ||
    `${notification?.type || "default"}-${Date.now()}`;

  return enqueueToast({
    id: `notif-${uniqueKey}`,
    kind: "system_notification",
    toastType,
    priority,
    debounceKey: `notif-${uniqueKey}`,
    groupKey: null,
    options: {
      position: isArabic ? "top-left" : "top-right",
      autoClose,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      rtl: isArabic,
      className,
      icon: false,
    },
    render: () => (
      <CustomToast
        icon={Icon}
        title={title}
        message={message}
        iconColor={iconColor}
        bgColor={bgColor}
        groupedCount={1}
      />
    ),
  });
}

export default function NotificationToast({ notification }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  useEffect(() => {
    if (!notification) return;

    const meta = getNotificationMeta(notification.type, isAr);
    const { Icon, toastType, iconColor, bgColor, priority } = meta;

    const title =
      notification.title || (isAr ? "إشعار جديد" : "New notification");
    const message = notification.message || "";

    enqueueNotificationToast({
      notification,
      title,
      message,
      Icon,
      toastType,
      iconColor,
      bgColor,
      isArabic: isAr,
      priority,
      className: `notification-toast notification-toast--${notification.type || "default"}`,
      autoClose: 5000,
    });
  }, [notification, isAr]);

  return null;
}

export const showCheckoutSuccess = (orderId, isArabic = false) => {
  return enqueueToast({
    kind: "checkout",
    toastType: "success",
    priority: TOAST_PRIORITY.HIGH,
    debounceKey: `checkout-success-${orderId}`,
    groupKey: `checkout-success-${orderId}`,
    options: {
      position: isArabic ? "top-left" : "top-right",
      autoClose: 6000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      rtl: isArabic,
      className: "custom-toast custom-toast--checkout-success",
      icon: false,
    },
    render: (item) => (
      <CustomToast
        icon={CheckCircle2}
        title={isArabic ? "تم إتمام الشراء!" : "Checkout Successful!"}
        message={
          isArabic
            ? `تم إنشاء طلبك رقم #${orderId} بنجاح. شكراً لتسوقك معنا!`
            : `Your order #${orderId} has been placed successfully. Thank you for shopping with us!`
        }
        iconColor="#16a34a"
        bgColor="rgba(34, 197, 94, 0.15)"
        groupedCount={item.groupedCount || 1}
      />
    ),
  });
};

export const showSystemToast = (notification, isArabic = false) => {
  if (!notification) return null;

  const meta = getNotificationMeta(notification.type, isArabic);
  const { Icon, toastType, iconColor, bgColor, priority } = meta;

  const title = notification.title || (isArabic ? "إشعار" : "Notification");
  const message = notification.message || "";

  return enqueueNotificationToast({
    notification,
    title,
    message,
    Icon,
    toastType,
    iconColor,
    bgColor,
    isArabic,
    priority,
    className: `notification-toast notification-toast--${notification.type || "default"}`,
    autoClose: 5000,
  });
};

export const showOrderToast = (orderId, isArabic = false) => {
  return enqueueToast({
    kind: "order",
    toastType: "warning",
    priority: TOAST_PRIORITY.HIGH,
    debounceKey: `order-toast-${orderId}`,
    groupKey: `order-toast-${orderId}`,
    options: {
      position: isArabic ? "top-left" : "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      rtl: isArabic,
      className: "custom-toast custom-toast--order",
      icon: false,
    },
    render: (item) => (
      <CustomToast
        icon={ShoppingBag}
        title={isArabic ? "تم إنشاء الطلب" : "Order Created"}
        message={
          isArabic
            ? `تم إنشاء طلبك رقم #${orderId} بنجاح`
            : `Your order #${orderId} has been created successfully`
        }
        iconColor="#f59e0b"
        bgColor="rgba(245, 158, 11, 0.15)"
        groupedCount={item.groupedCount || 1}
      />
    ),
  });
};

export const showPaymentToast = (amount, isArabic = false) => {
  return enqueueToast({
    kind: "payment",
    toastType: "success",
    priority: TOAST_PRIORITY.HIGH,
    debounceKey: `payment-toast-${amount}`,
    groupKey: `payment-toast-${amount}`,
    options: {
      position: isArabic ? "top-left" : "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      rtl: isArabic,
      className: "custom-toast custom-toast--payment",
      icon: false,
    },
    render: (item) => (
      <CustomToast
        icon={CreditCard}
        title={isArabic ? "تم الدفع بنجاح" : "Payment Successful"}
        message={
          isArabic
            ? `تم معالجة دفع بقيمة $${amount}`
            : `Payment of $${amount} processed successfully`
        }
        iconColor="#16a34a"
        bgColor="rgba(34, 197, 94, 0.15)"
        groupedCount={item.groupedCount || 1}
      />
    ),
  });
};
